import React from 'react';
import { Chip, ProgressBar } from '../../tcn/ui-kit';
import { TCN } from '../../tcn/ui-kit/tokens';

/**
 * Module 3 — A3.2-A3.5 tier metric sub-views, pixel-faithful to mockup
 * `module3-tiers.jsx`:
 *   - diamonds  (A3.2): MTD / Target / Last-Mo. + completion %
 *   - upgrades  (A3.3): {actual}/{target} upgraded + % + bar
 *   - maintains (A3.4): {actual}/{target} held + % + bar
 *   - process   (A3.5): Valid Go-LIVE % + PK Penetration % (2-col + bars)
 *
 * Props: view, row (manager_tier_current_month row)
 */
const fmtM = (raw) => `${(Number(raw || 0) / 1_000_000).toFixed(2)}M`;
const pctColor = (p) => (p == null ? TCN.textMute : p >= 80 ? TCN.green : p >= 50 ? TCN.amber : TCN.red);
const compColor = (p) => (p == null ? TCN.textMute : p >= 80 ? TCN.green : p >= 60 ? TCN.amber : TCN.red);

const card = {
  padding: 14, background: TCN.surface1, border: `1px solid ${TCN.border}`, borderRadius: 12,
};
const miniLabel = { fontSize: 9, color: TCN.textMute, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.4px' };

const TierMetricRow = ({ view, row }) => {
  if (!row) return null;
  const tier = row.tier;
  const n = row.creators_in_tier ?? 0;

  if (view === 'diamonds') {
    const comp = row.diamonds_completion_tier_pct;
    return (
      <div style={card}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Chip color="cyan" size="sm">T{tier}</Chip>
            <span style={{ fontSize: 12, color: TCN.textDim }}>{n} creators</span>
          </div>
          <span style={{ fontSize: 12, fontWeight: 700, color: compColor(comp), fontVariantNumeric: 'tabular-nums' }}>
            {comp == null ? '—' : `${Math.round(comp)}%`}
          </span>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10, marginTop: 12 }}>
          {[
            ['MTD', row.diamonds_mtd_tier_clear, TCN.text],
            ['Target', row.diamonds_mtd_target_tier_clear, TCN.textDim],
            ['Last Mo.', row.diamonds_last_month_tier_clear, TCN.textDim],
          ].map(([l, v, c]) => (
            <div key={l}>
              <div style={miniLabel}>{l}</div>
              <div style={{ fontSize: 16, fontWeight: 700, marginTop: 2, color: c, fontVariantNumeric: 'tabular-nums' }}>{fmtM(v)}</div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (view === 'upgrades' || view === 'maintains') {
    const isUp = view === 'upgrades';
    const actual = Number((isUp ? row.upgrade_actual_tier : row.maintain_actual_tier) ?? 0);
    const target = Number((isUp ? row.upgrade_target_tier : row.maintain_target_tier) ?? 0);
    const pct = target > 0 ? Math.round((actual / target) * 100) : null;
    return (
      <div style={{ ...card, display: 'flex', alignItems: 'center', gap: 14 }}>
        <Chip color="cyan" size="md">T{tier}</Chip>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
            <span style={{ fontSize: 13, fontWeight: 600 }}>
              <span style={{ color: isUp ? TCN.green : TCN.text }}>{actual}</span>
              <span style={{ color: TCN.textMute }}> / {target} {isUp ? 'upgraded' : 'held'}</span>
            </span>
            <span style={{ fontSize: 12, fontWeight: 700, color: pctColor(pct), fontVariantNumeric: 'tabular-nums' }}>
              {pct == null ? '—' : `${pct}%`}
            </span>
          </div>
          <div style={{ marginTop: 8 }}>
            <ProgressBar value={actual} target={target || 1} color="cyan" animated />
          </div>
        </div>
      </div>
    );
  }

  // process (A3.5)
  const gl = row.valid_go_live_rate_tier;
  const pk = row.pk_penetration_tier;
  return (
    <div style={card}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
        <Chip color="cyan" size="sm">T{tier}</Chip>
        <span style={{ fontSize: 12, color: TCN.textDim }}>{n} creators</span>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
        <div>
          <div style={miniLabel}>Valid Go-LIVE</div>
          <div style={{ fontSize: 22, fontWeight: 700, marginTop: 4, color: (gl ?? 0) >= 30 ? TCN.green : TCN.amber, fontVariantNumeric: 'tabular-nums' }}>
            {gl == null ? '—' : Math.round(gl)}<span style={{ fontSize: 12, color: TCN.textDim }}>%</span>
          </div>
          <ProgressBar value={gl ?? 0} target={30} color="cyan" />
        </div>
        <div>
          <div style={miniLabel}>PK Penetration</div>
          <div style={{ fontSize: 22, fontWeight: 700, marginTop: 4, color: (pk ?? 0) >= 60 ? TCN.green : TCN.amber, fontVariantNumeric: 'tabular-nums' }}>
            {pk == null ? '—' : Math.round(pk)}<span style={{ fontSize: 12, color: TCN.textDim }}>%</span>
          </div>
          <ProgressBar value={pk ?? 0} target={60} color="cyan" />
        </div>
      </div>
    </div>
  );
};

export default TierMetricRow;
